import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { register, sendPin } from '../Services/services';
import { useBranding } from '../Context/BrandingContext';
import { EMAIL_SUBSYSTEM_USER_MESSAGE, isEmailSubsystemNotice } from '../constants/authMessages';

function isEngagementAlreadyExists(message) {
  return typeof message === 'string' && /engagement already exists/i.test(message);
}

function Register({ onRegisterComplete, onBack, onCancel, variant = 'page' }) {
  const { email } = useSelector((state) => state.auth);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [partSales, setPartSales] = useState(false);
  const [mro, setMro] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { branding } = useBranding();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!name.trim() || !company.trim() || !phone.trim() || !jobTitle.trim()) {
      setError('Name, company, phone number, and job title are required');
      setLoading(false);
      return;
    }

    try {
      // 1. Create engagement in Salesforce
      const registerResponse = await register(email, name.trim(), company.trim(), phone.trim(), jobTitle.trim(), partSales, mro);
      
      const alreadyExists = isEngagementAlreadyExists(registerResponse.data?.message);
      if (!registerResponse.data?.success && !alreadyExists) {
        setError(registerResponse.data?.message || 'Registration failed');
        setLoading(false);
        return;
      }
      // If "engagement already exists": silently fall through to send PIN
      // — never expose to the submitter that this email is already registered.

      // 2. Send PIN code to email — advance to Verify regardless.
      //    If the email subsystem is down, Verify shows the friendly notice.
      let emailError = null;
      try {
        const sendResponse = await sendPin(email);
        if (!sendResponse.data?.success) {
          emailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
        }
      } catch (sendErr) {
        emailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
      }

      // 3. Transition to Verify screen, carrying any email-subsystem notice
      if (onRegisterComplete) {
        onRegisterComplete(emailError);
      }
    } catch (err) {
      // Check if the *exception* body indicates an existing engagement
      const errMsg = err.response?.data?.message || err.response?.data?.error || '';
      if (isEngagementAlreadyExists(errMsg)) {
        // Same silent path — send PIN and go to Verify
        let emailError = null;
        try {
          const sendResponse = await sendPin(email);
          if (!sendResponse.data?.success) emailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
        } catch (_) {
          emailError = EMAIL_SUBSYSTEM_USER_MESSAGE;
        }
        if (onRegisterComplete) onRegisterComplete(emailError);
        return;
      }
      console.error('Registration error:', err);
      setError(errMsg || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <>
      <div style={styles.modalHeader}>
        <h1 style={styles.modalTitle}>Create your account</h1>
        <p style={styles.modalSubtitle}>Complete registration to continue</p>
      </div>

      <div style={styles.modalEmailPill}>
        <span style={styles.modalEmailLabel}>Email</span>
        <span style={styles.modalEmailValue}>{email}</span>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={styles.inputGroup}>
            <label style={styles.modalLabel} htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
              style={styles.modalInput}
              disabled={loading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.modalLabel} htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Enter your company name"
              required
              style={styles.modalInput}
              disabled={loading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.modalLabel} htmlFor="phone">Phone Number</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              required
              style={styles.modalInput}
              disabled={loading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.modalLabel} htmlFor="jobTitle">Job Title</label>
            <input
              id="jobTitle"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Enter your job title"
              required
              style={styles.modalInput}
              disabled={loading}
            />
          </div>

          <div style={styles.interestsSection}>
            <label style={styles.modalLabel}>Interests</label>
            <div style={styles.checkboxRow}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={partSales}
                  onChange={(e) => setPartSales(e.target.checked)}
                  style={styles.checkbox}
                  disabled={loading}
                />
                Part Sales
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={mro}
                  onChange={(e) => setMro(e.target.checked)}
                  style={styles.checkbox}
                  disabled={loading}
                />
                MRO
              </label>
            </div>
          </div>

          {error && (
            <div style={isEmailSubsystemNotice(error) ? styles.modalInfo : styles.modalError}>{error}</div>
          )}

          <button 
            type="submit" 
            style={{ ...styles.modalPrimaryButton, backgroundColor: branding.primaryColor }}
            disabled={loading || !name.trim() || !company.trim() || !phone.trim() || !jobTitle.trim()}
          >
            {loading ? 'Creating Account...' : 'Continue'}
          </button>

          <button
            type="button"
            onClick={onBack}
            style={styles.modalSecondaryButton}
            disabled={loading}
          >
            Back
          </button>

          {variant === 'modal' && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={styles.modalTertiaryButton}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </form>

      <p style={styles.modalNote}>
        After registration, you’ll receive a verification code via email.
      </p>
    </>
  );

  if (variant === 'modal') {
    return <div>{content}</div>;
  }

  // Page (legacy) layout wrapper
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ ...styles.header, borderBottom: `2px solid ${branding.primaryColor}` }}>
          <p style={{ ...styles.companyName, color: branding.primaryColor }}>{branding.companyName}</p>
          <h1 style={{ ...styles.title, color: branding.primaryColor }}>Complete Registration</h1>
          <p style={styles.subtitle}>Create your account to continue</p>
        </div>
        <div style={styles.emailDisplay}>
          <span style={styles.emailLabel}>Email:</span>
          <span style={styles.emailValue}>{email}</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
              style={styles.input}
              disabled={loading}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Enter your company name"
              required
              style={styles.input}
              disabled={loading}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="phone">Phone Number</label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              required
              style={styles.input}
              disabled={loading}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="jobTitle">Job Title</label>
            <input
              id="jobTitle"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Enter your job title"
              required
              style={styles.input}
              disabled={loading}
            />
          </div>
          <div style={styles.interestsSection}>
            <label style={styles.interestsHeading}>Interests</label>
            <div style={styles.checkboxRow}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={partSales}
                  onChange={(e) => setPartSales(e.target.checked)}
                  style={styles.checkbox}
                  disabled={loading}
                />
                Part Sales
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={mro}
                  onChange={(e) => setMro(e.target.checked)}
                  style={styles.checkbox}
                  disabled={loading}
                />
                MRO
              </label>
            </div>
          </div>
          {error && (
            <div style={isEmailSubsystemNotice(error) ? styles.infoNotice : styles.error}>{error}</div>
          )}
          <button
            type="submit"
            style={{ ...styles.button, backgroundColor: branding.primaryColor }}
            disabled={loading || !name.trim() || !company.trim() || !phone.trim() || !jobTitle.trim()}
          >
            {loading ? 'Creating Account...' : 'Continue'}
          </button>
          <button
            type="button"
            onClick={onBack}
            style={{ ...styles.backButton, color: branding.primaryColor, border: `1px solid ${branding.primaryColor}` }}
            disabled={loading}
          >
            Back
          </button>
        </form>
        <p style={styles.note}>
          After registration, you'll receive a verification code via email.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '2px solid #0066cc',
  },
  companyName: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '28px',
    fontWeight: '700',
    color: '#0066cc',
  },
  subtitle: {
    margin: '0',
    color: '#666',
    fontSize: '16px',
  },
  emailDisplay: {
    backgroundColor: '#f8f9fa',
    padding: '12px 16px',
    borderRadius: '4px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  emailLabel: {
    color: '#666',
    fontSize: '14px',
  },
  emailValue: {
    color: '#333',
    fontWeight: '500',
    fontSize: '14px',
  },
  inputGroup: {
    marginBottom: '16px',
  },
  interestsSection: {
    marginBottom: '16px',
  },
  interestsHeading: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  checkboxRow: {
    display: 'flex',
    gap: '24px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  backButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#0066cc',
    border: '1px solid #0066cc',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  error: {
    color: '#d32f2f',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '8px 12px',
    backgroundColor: '#ffebee',
    borderRadius: '4px',
  },
  infoNotice: {
    color: '#1e40af',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '10px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '4px',
    border: '1px solid #bfdbfe',
    lineHeight: 1.45,
  },
  note: {
    marginTop: '20px',
    textAlign: 'center',
    color: '#666',
    fontSize: '13px',
  },

  // Modal variant (Figma Make-inspired)
  modalHeader: {
    marginBottom: 16,
  },
  modalTitle: {
    margin: '0 0 6px 0',
    fontSize: 22,
    fontWeight: 600,
    color: 'var(--foreground)',
  },
  modalSubtitle: {
    margin: 0,
    fontSize: 14,
    color: 'var(--muted-foreground)',
  },
  modalEmailPill: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    backgroundColor: 'var(--muted)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '10px 12px',
    marginBottom: 14,
  },
  modalEmailLabel: {
    fontSize: 12,
    color: 'var(--muted-foreground)',
  },
  modalEmailValue: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--foreground)',
  },
  modalLabel: {
    display: 'block',
    marginBottom: 6,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--foreground)',
  },
  modalInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--input-background)',
    color: 'var(--foreground)',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  modalPrimaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: 'none',
    color: 'var(--primary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 6,
  },
  modalSecondaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalTertiaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: 'none',
    backgroundColor: 'transparent',
    color: 'var(--muted-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalError: {
    color: 'var(--destructive)',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
  },
  modalInfo: {
    color: '#1e3a8a',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.35)',
    lineHeight: 1.45,
  },
  modalNote: {
    marginTop: 14,
    textAlign: 'center',
    color: 'var(--muted-foreground)',
    fontSize: 13,
  },
};

export default Register;
